/**
 * Support Worker Onboarding - internal test UI.
 * Mock form submission only. No backend, no storage, no banking details saved.
 */

interface WorkerOnboardingState {
  fullName: string;
  preferredName: string;
  providerType: ProviderTypeValue;
  email: string;
  phone: string;
  address: string;
  preferredContact: string;
  businessName: string;
  abn: string;
  insuranceStatus: string;
  childrenCheck: string;
  ndisScreening: string;
  serviceTypes: string[];
  availability: string[];
  travelRadius: string;
  languages: string;
  confirmed: boolean;
}

interface ReviewRow {
  label: string;
  value: string;
}

interface MockResult {
  workerId: string;
  timestamp: string;
}

type ProviderTypeValue = 'individual' | 'sole-trader-trading' | 'company' | 'organisation';

const PROVIDER_TYPE_OPTIONS: Array<{ value: ProviderTypeValue; label: string }> = [
  { value: 'individual', label: 'Individual / sole trader' },
  { value: 'sole-trader-trading', label: 'Sole trader with trading name' },
  { value: 'company', label: 'Company / Pty Ltd' },
  { value: 'organisation', label: 'Organisation / provider business' },
];

const SERVICE_TYPES = [
  'Community participation',
  'Daily living',
  'Transport',
  'Social activities',
  'Capacity building',
];

const AVAILABILITY_OPTIONS = ['Weekdays', 'Weekends', 'Evenings'];

const initialState: WorkerOnboardingState = {
  fullName: 'Jane Smith',
  preferredName: '',
  providerType: 'individual',
  email: 'jane.smith@example.com',
  phone: '0400 000 000',
  address: '123 Demo Street, Sydney NSW 2000',
  preferredContact: 'Email',
  businessName: 'Demo Support Services',
  abn: '00 000 000 000',
  insuranceStatus: 'Current',
  childrenCheck: 'Valid',
  ndisScreening: 'Cleared',
  serviceTypes: ['Community participation', 'Daily living'],
  availability: ['Weekdays'],
  travelRadius: '25 km',
  languages: 'English',
  confirmed: false,
};

let formState: WorkerOnboardingState = { ...initialState };
let mockResult: MockResult | null = null;

function injectStyles(): void {
  if (document.getElementById('sp-worker-onboarding-styles')) return;
  const style = document.createElement('style');
  style.id = 'sp-worker-onboarding-styles';
  style.textContent = `
body.sp-public-body {
  display: block !important;
  min-height: 100dvh;
  height: auto !important;
  overflow-y: auto !important;
  background: var(--bg) !important;
}
.sp-public-root {
  display: block !important;
  width: 100% !important;
  min-height: 100dvh !important;
  height: auto !important;
  overflow: visible !important;
  flex: none !important;
  background: var(--bg);
}
.sp-worker-page {
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: var(--bg);
  color: var(--fg);
  font-family: var(--font-sans);
  padding: 24px;
}
.sp-worker-shell {
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
}
.sp-worker-topbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
}
.sp-worker-brand {
  color: var(--fg-muted);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
.sp-worker-header h1 {
  margin: 6px 0 6px;
  color: var(--fg);
  font-size: 24px;
  line-height: 1.2;
  font-weight: 800;
}
.sp-worker-header p {
  margin: 0;
  color: var(--fg-muted);
  font-size: 14px;
  line-height: 1.45;
}
.sp-worker-badges {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}
.sp-worker-badge {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 3px 9px;
  border-radius: 4px;
  border: 1px solid var(--border);
  background: var(--bg-secondary);
  color: var(--fg-muted);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0;
}
.sp-worker-badge-warning {
  border-color: var(--warning);
  color: var(--warning);
}
.sp-worker-warning {
  margin-bottom: 16px;
  padding: 12px 14px;
  border: 1px solid var(--warning);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--fg);
  font-size: 13px;
  line-height: 1.45;
  font-weight: 700;
}
.sp-worker-form,
.sp-worker-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.sp-worker-card {
  min-width: 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg-secondary);
  padding: 14px;
}
.sp-worker-card-wide {
  grid-column: 1 / -1;
}
.sp-worker-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.sp-worker-eyebrow {
  color: var(--fg-muted);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.sp-worker-card-title {
  margin-top: 3px;
  color: var(--fg);
  font-size: 15px;
  font-weight: 800;
}
.sp-worker-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.sp-worker-field {
  min-width: 0;
}
.sp-worker-field-wide {
  grid-column: 1 / -1;
}
.sp-worker-label {
  display: block;
  margin-bottom: 5px;
  color: var(--fg-muted);
  font-size: 12px;
  font-weight: 700;
}
.sp-worker-input,
.sp-worker-select {
  width: 100%;
  min-height: 38px;
  box-sizing: border-box;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
  color: var(--fg);
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
}
.sp-worker-readonly {
  min-height: 38px;
  box-sizing: border-box;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
  color: var(--fg);
  padding: 9px 10px;
  font-size: 13px;
  line-height: 1.35;
}
.sp-worker-status {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid var(--warning);
  background: var(--bg-tertiary);
  color: var(--warning);
  font-size: 11px;
  font-weight: 700;
}
.sp-worker-check-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
.sp-worker-check {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 9px 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
  color: var(--fg);
  font-size: 13px;
  line-height: 1.35;
}
.sp-worker-check input {
  flex: 0 0 auto;
  accent-color: var(--accent);
}
.sp-worker-disclaimer {
  margin: 0;
  color: var(--fg);
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-line;
}
.sp-worker-notice {
  margin-top: 12px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
  color: var(--fg);
  font-size: 13px;
  line-height: 1.55;
}
.sp-worker-review-list,
.sp-worker-result-list,
.sp-worker-audit-list {
  display: grid;
  gap: 8px;
}
.sp-worker-review-item,
.sp-worker-result-item,
.sp-worker-audit-item {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
}
.sp-worker-review-name,
.sp-worker-result-name {
  color: var(--fg-muted);
  font-size: 12px;
  font-weight: 700;
}
.sp-worker-review-value,
.sp-worker-result-value,
.sp-worker-audit-item {
  color: var(--fg);
  font-size: 12px;
  font-weight: 700;
  text-align: right;
  word-break: break-word;
}
.sp-worker-audit-item {
  display: block;
  text-align: left;
}
.sp-worker-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
}
.sp-worker-button {
  min-height: 38px;
  border: 1px solid var(--accent);
  border-radius: 6px;
  background: var(--accent);
  color: var(--bg);
  padding: 8px 14px;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}
.sp-worker-button-secondary {
  border-color: var(--border);
  background: var(--bg);
  color: var(--fg);
}
.sp-worker-button:disabled {
  border-color: var(--border);
  background: var(--bg-tertiary);
  color: var(--fg-muted);
  cursor: not-allowed;
}
.sp-worker-result-title {
  margin: 0 0 10px;
  color: var(--fg);
  font-size: 18px;
  line-height: 1.25;
  font-weight: 800;
}
.sp-worker-result-warning {
  margin-bottom: 14px;
  padding: 12px;
  border: 1px solid var(--danger);
  border-radius: 6px;
  background: var(--bg);
  color: var(--danger);
  font-size: 13px;
  line-height: 1.45;
  font-weight: 800;
}
.sp-worker-result-footer {
  margin-top: 14px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
  color: var(--fg-muted);
  font-size: 13px;
  line-height: 1.55;
}
@media (max-width: 820px) {
  .sp-worker-page { padding: 16px; }
  .sp-worker-topbar { flex-direction: column; }
  .sp-worker-badges { justify-content: flex-start; }
  .sp-worker-form,
  .sp-worker-grid,
  .sp-worker-fields,
  .sp-worker-check-grid { grid-template-columns: 1fr; }
  .sp-worker-review-item,
  .sp-worker-result-item { flex-direction: column; gap: 3px; }
  .sp-worker-review-value,
  .sp-worker-result-value { text-align: left; }
  .sp-worker-actions { justify-content: stretch; }
  .sp-worker-button { width: 100%; }
}
`;
  document.head.appendChild(style);
}

function esc(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

function shell(): HTMLElement {
  document.title = 'Support Worker Onboarding - Hub People';
  injectStyles();

  document.body.classList.add('sp-public-body');
  const root = (document.getElementById('app') ?? document.body) as HTMLElement;
  root.classList.add('sp-public-root');
  root.innerHTML = '';

  const hide = (id: string): void => {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  };
  hide('sidebar-scrim');
  hide('modal-overlay');
  hide('toast');

  const page = document.createElement('div');
  page.className = 'sp-worker-page';
  root.appendChild(page);
  return page;
}

function inputField(id: keyof WorkerOnboardingState, label: string, value: string, wide = false): string {
  return `
    <label class="sp-worker-field${wide ? ' sp-worker-field-wide' : ''}" for="${esc(String(id))}">
      <span class="sp-worker-label">${esc(label)}</span>
      <input class="sp-worker-input" id="${esc(String(id))}" data-field="${esc(String(id))}" type="text" value="${esc(value)}">
    </label>
  `;
}

function selectField(id: keyof WorkerOnboardingState, label: string, value: string, options: string[]): string {
  return `
    <label class="sp-worker-field" for="${esc(String(id))}">
      <span class="sp-worker-label">${esc(label)}</span>
      <select class="sp-worker-select" id="${esc(String(id))}" data-field="${esc(String(id))}">
        ${options.map(option => `<option value="${esc(option)}"${option === value ? ' selected' : ''}>${esc(option)}</option>`).join('')}
      </select>
    </label>
  `;
}

function providerTypeSelectField(): string {
  return `
    <label class="sp-worker-field" for="providerType">
      <span class="sp-worker-label">Provider type</span>
      <select class="sp-worker-select" id="providerType" data-field="providerType">
        ${PROVIDER_TYPE_OPTIONS.map(option => `<option value="${esc(option.value)}"${option.value === formState.providerType ? ' selected' : ''}>${esc(option.label)}</option>`).join('')}
      </select>
    </label>
  `;
}

function checkboxGroup(name: 'serviceTypes' | 'availability', options: string[], selected: string[]): string {
  return `
    <div class="sp-worker-check-grid">
      ${options.map(option => `
        <label class="sp-worker-check">
          <input type="checkbox" data-list="${esc(name)}" value="${esc(option)}"${selected.includes(option) ? ' checked' : ''}>
          <span>${esc(option)}</span>
        </label>
      `).join('')}
    </div>
  `;
}

function reviewRows(): ReviewRow[] {
  const providerTypeLabel = PROVIDER_TYPE_OPTIONS.find(option => option.value === formState.providerType)?.label ?? formState.providerType;
  return [
    { label: 'Full name', value: formState.fullName },
    { label: 'Preferred name', value: formState.preferredName || 'Not provided' },
    { label: 'Role', value: 'Peer support worker' },
    { label: 'Provider type', value: providerTypeLabel },
    { label: 'Email', value: formState.email },
    { label: 'Phone', value: formState.phone },
    { label: 'Address', value: formState.address },
    { label: 'Preferred contact', value: formState.preferredContact },
    { label: 'Business name', value: formState.businessName },
    { label: 'ABN', value: formState.abn },
    { label: 'Insurance status', value: formState.insuranceStatus },
    { label: 'Working with children check', value: formState.childrenCheck },
    { label: 'NDIS Worker Screening', value: formState.ndisScreening },
    { label: 'Services', value: formState.serviceTypes.join(', ') || 'None selected' },
    { label: 'Availability', value: formState.availability.join(', ') || 'None selected' },
    { label: 'Travel radius', value: formState.travelRadius },
    { label: 'Languages', value: formState.languages },
    { label: 'Payment details', value: 'Masked demo placeholder only' },
  ];
}

function randomWorkerId(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let id = '';
  for (let i = 0; i < 6; i += 1) {
    id += chars[Math.floor(Math.random() * chars.length)];
  }
  return `SW-DEMO-${id}`;
}

function renderForm(): string {
  const deletionNotice = 'To request deletion or modification of your submitted profile information, email accounts@the-hub-ecosystems.com. Where possible, send the request from the same email address used when submitting the information. If that email was entered incorrectly, is unavailable, or may be slightly different, please explain this in your request. Additional verification may be required before records are changed or deleted.';
  const disclaimer = `By submitting this onboarding form, you acknowledge that:
- Your information will be used to create a support worker profile
- You consent to verification checks where applicable
- You can request modification or deletion of your profile at any time
- This platform complies with Australian Privacy Principles`;

  return `
    <form class="sp-worker-form" id="support-worker-form">
      <section class="sp-worker-card" aria-label="Worker or Provider Identity">
        <div class="sp-worker-card-head">
          <div>
            <div class="sp-worker-eyebrow">Section 1</div>
            <div class="sp-worker-card-title">Worker/Provider Identity</div>
          </div>
        </div>
        <div class="sp-worker-fields">
          ${inputField('fullName', 'Full name', formState.fullName)}
          ${inputField('preferredName', 'Preferred name', formState.preferredName)}
          <div class="sp-worker-field">
            <span class="sp-worker-label">Role</span>
            <div class="sp-worker-readonly">Peer support worker</div>
          </div>
          ${providerTypeSelectField()}
        </div>
      </section>

      <section class="sp-worker-card" aria-label="Contact Details">
        <div class="sp-worker-card-head">
          <div>
            <div class="sp-worker-eyebrow">Section 2</div>
            <div class="sp-worker-card-title">Contact Details</div>
          </div>
        </div>
        <div class="sp-worker-fields">
          ${inputField('email', 'Email', formState.email)}
          ${inputField('phone', 'Phone', formState.phone)}
          ${inputField('address', 'Address', formState.address, true)}
          ${selectField('preferredContact', 'Preferred contact method', formState.preferredContact, ['Email', 'Phone', 'Either'])}
        </div>
      </section>

      <section class="sp-worker-card sp-worker-card-wide" aria-label="Business and Admin Details">
        <div class="sp-worker-card-head">
          <div>
            <div class="sp-worker-eyebrow">Section 3</div>
            <div class="sp-worker-card-title">Business/Admin Details</div>
          </div>
        </div>
        <div class="sp-worker-fields">
          ${inputField('businessName', 'Business name', formState.businessName)}
          ${inputField('abn', 'ABN', formState.abn)}
          ${selectField('insuranceStatus', 'Insurance status', formState.insuranceStatus, ['Current', 'Expired', 'Not required', 'Pending'])}
          ${selectField('childrenCheck', 'Working with children check', formState.childrenCheck, ['Valid', 'Expired', 'Not applicable'])}
          ${selectField('ndisScreening', 'NDIS Worker Screening', formState.ndisScreening, ['Cleared', 'Pending', 'Not required'])}
        </div>
      </section>

      <section class="sp-worker-card" aria-label="Payment Details">
        <div class="sp-worker-card-head">
          <div>
            <div class="sp-worker-eyebrow">Section 4</div>
            <div class="sp-worker-card-title">Payment Details</div>
          </div>
          <span class="sp-worker-status">Not verified - demo placeholder only</span>
        </div>
        <div class="sp-worker-fields">
          <div class="sp-worker-field"><span class="sp-worker-label">BSB</span><div class="sp-worker-readonly">XXX-XXX</div></div>
          <div class="sp-worker-field"><span class="sp-worker-label">Account</span><div class="sp-worker-readonly">XXXX-XXXX</div></div>
          <div class="sp-worker-field"><span class="sp-worker-label">Account name</span><div class="sp-worker-readonly">J Smith</div></div>
          <div class="sp-worker-field sp-worker-field-wide"><div class="sp-worker-notice">No banking details are stored in this demo.</div></div>
        </div>
      </section>

      <section class="sp-worker-card" aria-label="Services and Preferences">
        <div class="sp-worker-card-head">
          <div>
            <div class="sp-worker-eyebrow">Section 5</div>
            <div class="sp-worker-card-title">Services &amp; Preferences</div>
          </div>
        </div>
        <div class="sp-worker-fields">
          <div class="sp-worker-field sp-worker-field-wide">
            <span class="sp-worker-label">Service types</span>
            ${checkboxGroup('serviceTypes', SERVICE_TYPES, formState.serviceTypes)}
          </div>
          <div class="sp-worker-field sp-worker-field-wide">
            <span class="sp-worker-label">Availability</span>
            ${checkboxGroup('availability', AVAILABILITY_OPTIONS, formState.availability)}
          </div>
          ${inputField('travelRadius', 'Travel radius', formState.travelRadius)}
          ${inputField('languages', 'Languages', formState.languages)}
        </div>
      </section>

      <section class="sp-worker-card sp-worker-card-wide" aria-label="Compliance and Privacy">
        <div class="sp-worker-card-head">
          <div>
            <div class="sp-worker-eyebrow">Section 6</div>
            <div class="sp-worker-card-title">Compliance &amp; Privacy</div>
          </div>
        </div>
        <p class="sp-worker-disclaimer">${esc(disclaimer)}</p>
        <div class="sp-worker-notice">${esc(deletionNotice)}</div>
      </section>

      <section class="sp-worker-card sp-worker-card-wide" aria-label="Review and Confirmation">
        <div class="sp-worker-card-head">
          <div>
            <div class="sp-worker-eyebrow">Section 7</div>
            <div class="sp-worker-card-title">Review &amp; Confirmation</div>
          </div>
        </div>
        <div class="sp-worker-review-list">
          ${reviewRows().map(row => `
            <div class="sp-worker-review-item">
              <span class="sp-worker-review-name">${esc(row.label)}</span>
              <span class="sp-worker-review-value">${esc(row.value)}</span>
            </div>
          `).join('')}
        </div>
        <label class="sp-worker-check" for="confirmed" style="margin-top: 12px;">
          <input type="checkbox" id="confirmed" data-confirmed="true"${formState.confirmed ? ' checked' : ''}>
          <span>I confirm the above details are correct (demo only)</span>
        </label>
        <div class="sp-worker-actions">
          <button class="sp-worker-button" type="submit"${formState.confirmed ? '' : ' disabled'}>Submit Mock Onboarding</button>
        </div>
      </section>
    </form>
  `;
}

function renderResult(result: MockResult): string {
  const rows: ReviewRow[] = [
    { label: 'Fake worker ID', value: result.workerId },
    { label: 'Profile status', value: 'Registered (demo)' },
    { label: 'Agreement status', value: 'Pending creation (demo)' },
    { label: 'Payment verification', value: 'Not verified - demo only' },
    { label: 'NDIS screening', value: 'Pending verification (demo)' },
    { label: 'Insurance', value: 'Pending verification (demo)' },
  ];
  const auditLog = [
    `[${result.timestamp}] Profile submitted (demo)`,
    `[${result.timestamp}] Verification checks queued (demo)`,
    `[${result.timestamp}] Agreement draft created (demo)`,
    `[${result.timestamp}] Payment details NOT stored (demo)`,
  ];

  return `
    <section class="sp-worker-card sp-worker-card-wide" aria-label="Mock Onboarding Result">
      <h2 class="sp-worker-result-title">MOCK ONBOARDING RESULT</h2>
      <div class="sp-worker-result-warning">This is a simulated result. No data was stored. No profile was created.</div>
      <div class="sp-worker-result-list">
        ${rows.map(row => `
          <div class="sp-worker-result-item">
            <span class="sp-worker-result-name">${esc(row.label)}</span>
            <span class="sp-worker-result-value">${esc(row.value)}</span>
          </div>
        `).join('')}
      </div>
      <div class="sp-worker-card-title" style="margin: 14px 0 8px;">Mock audit log</div>
      <div class="sp-worker-audit-list">
        ${auditLog.map(item => `<div class="sp-worker-audit-item">${esc(item)}</div>`).join('')}
      </div>
      <div class="sp-worker-result-footer">
        In production, the backend would create: support worker profile, service agreement draft, verification task queue, activity log entries, notification to admin.
      </div>
      <div class="sp-worker-actions">
        <button class="sp-worker-button sp-worker-button-secondary" type="button" id="reset-demo">Reset Demo</button>
      </div>
    </section>
  `;
}

function syncField(field: keyof WorkerOnboardingState, value: string): void {
  if (field === 'confirmed' || field === 'serviceTypes' || field === 'availability') return;
  if (field === 'providerType') {
    const allowed = PROVIDER_TYPE_OPTIONS.map(option => option.value);
    formState = {
      ...formState,
      providerType: allowed.includes(value as ProviderTypeValue) ? value as ProviderTypeValue : 'individual',
    };
    return;
  }
  formState = { ...formState, [field]: value };
}

function syncList(name: 'serviceTypes' | 'availability', value: string, checked: boolean): void {
  const current = formState[name];
  const next = checked ? [...current, value] : current.filter(item => item !== value);
  formState = { ...formState, [name]: next };
}

function renderContent(page: HTMLElement): void {
  page.innerHTML = `
    <main class="sp-worker-shell">
      <div class="sp-worker-topbar">
        <header class="sp-worker-header">
          <div class="sp-worker-brand">the hub ecosystems</div>
          <h1>Support Worker Onboarding</h1>
          <p>Internal test UI - mock submission only</p>
        </header>
        <div class="sp-worker-badges" aria-label="Demo warnings">
          <span class="sp-worker-badge sp-worker-badge-warning">Auth-gated</span>
          <span class="sp-worker-badge sp-worker-badge-warning">No backend storage</span>
          <span class="sp-worker-badge sp-worker-badge-warning">Mock submit</span>
        </div>
      </div>

      <div class="sp-worker-warning">
        DEMO ONLY. No real data stored. No real profiles created. No banking details saved. No invoices generated.
      </div>

      ${mockResult ? `<div class="sp-worker-grid">${renderResult(mockResult)}</div>` : renderForm()}
    </main>
  `;

  wireEvents(page);
}

function wireEvents(page: HTMLElement): void {
  page.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-field]').forEach(input => {
    input.addEventListener('input', () => {
      syncField(input.dataset['field'] as keyof WorkerOnboardingState, input.value);
      renderContent(page);
    });
    input.addEventListener('change', () => {
      syncField(input.dataset['field'] as keyof WorkerOnboardingState, input.value);
      renderContent(page);
    });
  });

  page.querySelectorAll<HTMLInputElement>('[data-list]').forEach(input => {
    input.addEventListener('change', () => {
      const listName = input.dataset['list'];
      if (listName === 'serviceTypes' || listName === 'availability') {
        syncList(listName, input.value, input.checked);
        renderContent(page);
      }
    });
  });

  const confirmed = page.querySelector<HTMLInputElement>('[data-confirmed]');
  confirmed?.addEventListener('change', () => {
    formState = { ...formState, confirmed: confirmed.checked };
    renderContent(page);
  });

  const form = page.querySelector<HTMLFormElement>('#support-worker-form');
  form?.addEventListener('submit', event => {
    event.preventDefault();
    if (!formState.confirmed) return;
    mockResult = {
      workerId: randomWorkerId(),
      timestamp: new Date().toLocaleString('en-AU', {
        timeZone: 'Australia/Sydney',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }),
    };
    renderContent(page);
  });

  const reset = page.querySelector<HTMLButtonElement>('#reset-demo');
  reset?.addEventListener('click', () => {
    formState = {
      ...initialState,
      serviceTypes: [...initialState.serviceTypes],
      availability: [...initialState.availability],
    };
    mockResult = null;
    renderContent(page);
  });
}

export function isSupportWorkerOnboardingPath(): boolean {
  return window.location.pathname.includes('/support-worker-onboarding');
}

export function renderSupportWorkerOnboarding(): void {
  formState = {
    ...initialState,
    serviceTypes: [...initialState.serviceTypes],
    availability: [...initialState.availability],
  };
  mockResult = null;
  const page = shell();
  renderContent(page);
}
